import type { Language } from '@offer-catalog/services/i18n'
import type { RefObject } from 'react'
import type { CustomViewSelections, CustomViewTarget } from './customViewConfig'
import type { GalleryProducts, ViewMode } from '@/types/catalog'
import { createContext } from 'react'

export const MIN_COLUMNS = 4
export const MAX_COLUMNS = 6

export type { ViewMode } from '@/types/catalog'

export interface FilterGroup {
  id: string
  filters: Filter[]
}

export interface Filter {
  id: string
  label: Record<Language, string>
  count: number
}

export type SortCriteria
  = | 'availability'
    | 'segmentation'
    | 'product-nature'
    | 'price'
    // | 'customer-rating'
    // | 'gmv'

export type SortOrder = 'asc' | 'desc'

export type ProductOrder
  = | 'AVAILABILITY_ASC'
    | 'AVAILABILITY_DESC'
    | 'SEGMENTATION_ASC'
    | 'SEGMENTATION_DESC'
    | 'PRODUCT_NATURE_ASC'
    | 'PRODUCT_NATURE_DESC'
    | 'PRICE_ASC'
    | 'PRICE_DESC'
    // | 'CUSTOMER_RATING_ASC'
    // | 'CUSTOMER_RATING_DESC'
    // | 'GMV_ASC'
    // | 'GMV_DESC'

export const SORT_CRITERIA: SortCriteria[] = [
  'availability',
  'segmentation',
  'product-nature',
  'price',
  // 'customer-rating'
  // 'gmv'
]

export const SORT_ORDERS = new Set<ProductOrder>([
  'AVAILABILITY_ASC',
  'AVAILABILITY_DESC',
  'SEGMENTATION_ASC',
  'SEGMENTATION_DESC',
  'PRODUCT_NATURE_ASC',
  'PRODUCT_NATURE_DESC',
  'PRICE_ASC',
  'PRICE_DESC',
  // 'CUSTOMER_RATING_ASC',
  // 'CUSTOMER_RATING_DESC',
  // 'GMV_ASC',
  // 'GMV_DESC',
])

export function getProductOrder(criteria: SortCriteria, order: SortOrder): ProductOrder | undefined {
  if (!SORT_CRITERIA.includes(criteria))
    return undefined

  return `${criteria.replace('-', '_').toUpperCase()}_${order.toUpperCase()}` as ProductOrder
}

export const DEFAULT_SORT_ORDERS: Record<SortCriteria, SortOrder> = {
  'availability': 'desc',
  'segmentation': 'asc',
  'product-nature': 'asc',
  'price': 'asc',
  // 'customer-rating': 'desc',
  // 'gmv': 'desc',
}

export const GROUP_BY_SM_CRITERIA: SortCriteria[] = [
  'segmentation',
  'product-nature',
]

export interface ProductsContextType {
  columns: number
  viewMode: ViewMode
  sortCriteria: SortCriteria
  sortOrder: SortOrder
  sortOrderChosen: boolean
  groupBySM: boolean
  customViewTarget: CustomViewTarget
  customViewSelections: CustomViewSelections
  isCustomViewOpen: boolean
  zoomIn: () => void
  zoomOut: () => void
  setViewMode: (mode: ViewMode) => void
  onSortCriteria: (criteria: SortCriteria) => void
  toggleSortOrder: () => void
  onCustomViewSelections: (selections: CustomViewSelections) => void
  setIsFiltersOpen: (isOpen: boolean) => void
  isFiltersOpen: boolean
  filterGroups: FilterGroup[]
  facetQueryKey: string
  isFetchingFacets: boolean
  loadFacetGroupLabels: (groupId: string) => Promise<void>
  loadingFacetGroups: ReadonlySet<string>
  openCustomView: () => void
  closeCustomView: () => void
  products: GalleryProducts['data']
  pages: GalleryProducts[]
  sentinelRef: RefObject<HTMLDivElement | null>
  isFetchingNextPage: boolean
  isRefreshing: boolean
  hasNextPage: boolean
  isLoading: boolean
  isError: boolean
}

export const ProductsContext
  = createContext<ProductsContextType | null>(null)
